/**
 * KategoriePicker — the IGB2B category multi-select, grouped by the 9
 * Branchenfamilien (build-spec §Web). Each family is a heading with a
 * "select all in family" checkbox + its categories as checkboxes. The
 * selection is the set of Branchencodes.
 */

import { useMemo } from 'react';
import { IGB2B_FAMILIEN, type Igb2bKategorieDto } from '../types';

interface Props {
  kategorien: Igb2bKategorieDto[];
  /** Currently selected Branchencodes. */
  selected: string[];
  onChange: (branchencodes: string[]) => void;
  disabled?: boolean;
}

export function KategoriePicker({ kategorien, selected, onChange, disabled }: Props) {
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  // Family key (family-head code) → its categories (in code order), only families that have any.
  const byFamilie = useMemo(() => {
    const map = new Map<string, Igb2bKategorieDto[]>();
    for (const fam of IGB2B_FAMILIEN) map.set(fam.key, []);
    for (const k of kategorien) {
      const list = map.get(k.familie);
      if (list) list.push(k);
    }
    return map;
  }, [kategorien]);

  const toggle = (code: string) => {
    if (disabled) return;
    const next = new Set(selectedSet);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    onChange([...next]);
  };

  const toggleFamilie = (codes: string[], allOn: boolean) => {
    if (disabled) return;
    const next = new Set(selectedSet);
    for (const c of codes) {
      if (allOn) next.delete(c);
      else next.add(c);
    }
    onChange([...next]);
  };

  return (
    <div className="flex flex-col gap-4" role="group" aria-label="Versicherungskategorien">
      {IGB2B_FAMILIEN.map((fam) => {
        const cats = byFamilie.get(fam.key) ?? [];
        if (cats.length === 0) return null;
        const codes = cats.map((c) => c.branchencode);
        const selCount = codes.filter((c) => selectedSet.has(c)).length;
        const allOn = selCount === codes.length;
        return (
          <fieldset key={fam.key} className="rounded-lg border border-border bg-card p-3">
            <legend className="flex w-full items-center justify-between px-1">
              <span className="text-sm font-semibold text-foreground">{fam.label}</span>
              <button
                type="button"
                disabled={disabled}
                onClick={() => toggleFamilie(codes, allOn)}
                className="text-xs text-primary hover:underline disabled:text-muted-foreground disabled:no-underline"
              >
                {allOn ? 'Alle abwählen' : 'Alle wählen'}
                <span className="ml-1 tabular-nums text-muted-foreground">
                  ({selCount}/{codes.length})
                </span>
              </button>
            </legend>
            <div className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {cats.map((c) => {
                const on = selectedSet.has(c.branchencode);
                return (
                  <label
                    key={c.branchencode}
                    className="flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 text-sm hover:bg-muted/60"
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      disabled={disabled}
                      onChange={() => toggle(c.branchencode)}
                      className="size-4 shrink-0 rounded border-border"
                      aria-label={c.deBezeichnung}
                    />
                    <span className="min-w-0 truncate text-foreground">{c.deBezeichnung}</span>
                    <span className="ml-auto shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground">
                      {c.branchencode}
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        );
      })}
    </div>
  );
}
